// Shared layout for the four /comparatif/* competitor pages.
//
// One file means: one place to fix when copy register or layout shifts;
// no risk of three pages drifting from the fourth as we iterate. Each
// individual /comparatif/<slug> page is a four-line shell that picks
// its competitor from the matrix and renders this component.

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, ExternalLink, Minus, X } from "lucide-react";
import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { CTABanner } from "@/components/marketing/CTABanner";
import { Rule } from "@/components/marketing/Rule";
import { Badge } from "@/components/ui/badge";
import {
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
  buildOrganizationJsonLd,
} from "@/lib/seo";
import { withUtm } from "@/lib/marketing-utm";
import {
  type CellVerdict,
  type CompetitorDefinition,
  locText,
} from "@/lib/marketing-competitor-matrix";
import { useLocale, type Locale } from "@/lib/locale";

export interface CompetitorComparisonLayoutProps {
  competitor: CompetitorDefinition;
}

export function CompetitorComparisonLayout({ competitor: c }: CompetitorComparisonLayoutProps) {
  const { t, i18n } = useTranslation();
  const locale = useLocale();

  const path = `/comparatif/${c.slug}`;
  const heroTitle = locText(c.heroTitle, locale);
  const heroLead = locText(c.heroLead, locale);

  const jsonLd = [
    buildOrganizationJsonLd(),
    buildBreadcrumbJsonLd([
      { name: t("marketing.breadcrumbs.home"), path: "/" },
      { name: t("marketing.pages.comparisons.breadcrumb"), path: "/comparatif" },
      { name: c.competitorName, path },
    ]),
    buildArticleJsonLd({
      title: heroTitle,
      description: heroLead,
      path,
      locale,
      section: t("marketing.pages.comparisons.breadcrumb"),
      // Evergreen marketing comparison — Article (not NewsArticle) avoids
      // Google's stricter News-source signals. Stable published date keeps
      // the JSON-LD valid across renders.
      articleType: "Article",
      publishedAt: "2026-05-15",
      modifiedAt: "2026-05-15",
      authorName: "L'équipe delapresse",
    }),
  ];

  return (
    <>
      <SeoHead title={heroTitle} description={heroLead} path={path} locale={locale} type="article" jsonLd={jsonLd} />

      {/* Hero */}
      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.comparisons.breadcrumb")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">
            {heroTitle}
          </DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{heroLead}</p>

          <a
            href={c.homepageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-sm text-accent-press hover:underline"
          >
            {t("marketing.pages.comparisons.competitorHomepage", { name: c.competitorName })}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        </div>
      </Section>

      <Rule />

      {/* Honest summary box */}
      <Section padding="md">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <article className="border border-rule bg-background p-6">
            <h2 className="dateline">
              {t("marketing.pages.comparisons.summary.chooseCompetitor", { name: c.competitorName })}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground">
              {locText(c.summaryChooseCompetitor, locale)}
            </p>
          </article>
          <article className="border-2 border-foreground bg-background p-6">
            <h2 className="dateline">{t("marketing.pages.comparisons.summary.chooseDelapresse")}</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground">
              {locText(c.summaryChooseDelapresse, locale)}
            </p>
          </article>
        </div>
      </Section>

      <Rule />

      {/* Feature-by-feature comparison */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.comparisons.features.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.comparisons.features.title")}
          </DisplayHeading>
        </div>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-foreground">
                <th className="px-3 py-3 text-left font-medium">
                  {t("marketing.pages.comparisons.features.featureHeader")}
                </th>
                <th className="px-3 py-3 text-left font-medium">delapresse</th>
                <th className="px-3 py-3 text-left font-medium">{c.competitorName}</th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map((row) => (
                <tr key={row.id} className="border-b border-rule/50">
                  <th scope="row" className="px-3 py-3 text-left font-normal text-foreground">
                    {locText(row.label, locale)}
                  </th>
                  <td className="px-3 py-3">
                    <VerdictCell cell={row.delapresse} locale={locale} />
                  </td>
                  <td className="px-3 py-3">
                    <VerdictCell cell={row.competitor} locale={locale} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Rule />

      {/* Use-case fit verdict */}
      <Section padding="md" tone="paper">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.comparisons.fit.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.comparisons.fit.title")}
          </DisplayHeading>
        </div>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-ink/80">
                <th className="px-3 py-3 text-left font-medium text-ink">
                  {t("marketing.pages.comparisons.fit.audienceHeader")}
                </th>
                <th className="px-3 py-3 text-left font-medium text-ink">delapresse</th>
                <th className="px-3 py-3 text-left font-medium text-ink">{c.competitorName}</th>
              </tr>
            </thead>
            <tbody>
              {c.useCaseFit.map((row) => (
                <tr key={row.id} className="border-b border-ink/15">
                  <th scope="row" className="px-3 py-3 text-left font-normal text-ink">
                    {locText(row.audience, locale)}
                  </th>
                  <td className="px-3 py-3 text-ink/85">{locText(row.delapresseVerdict, locale)}</td>
                  <td className="px-3 py-3 text-ink/85">{locText(row.competitorVerdict, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Pricing comparison */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.comparisons.pricing.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.comparisons.pricing.title")}
          </DisplayHeading>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          <article className="border-2 border-foreground bg-background p-6">
            <h3 className="text-lg font-medium tracking-tight">delapresse</h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground">{locText(c.pricingDelapresse, locale)}</p>
            <Link
              to={withUtm("/tarifs", { source: "comparison", campaign: c.slug })}
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-accent-press hover:underline"
            >
              {t("marketing.pages.comparisons.pricing.detailedPricing")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </article>
          <article className="border border-rule bg-background p-6">
            <h3 className="text-lg font-medium tracking-tight">{c.competitorName}</h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground">{locText(c.pricingCompetitor, locale)}</p>
          </article>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
          {locText(c.pricingNote, locale)}
        </p>

        {c.sourceUrls.length > 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t("marketing.pages.comparisons.pricing.sourcesLabel")} {" "}
            {c.sourceUrls.map((url, i) => (
              <span key={url}>
                <a href={url} target="_blank" rel="noopener noreferrer" className="underline">
                  {new URL(url).host}
                </a>
                {i < c.sourceUrls.length - 1 ? ", " : ""}
              </span>
            ))}
          </p>
        )}
      </Section>

      <Rule />

      {/* Migration mini-section */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.comparisons.migration.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.comparisons.migration.title", { name: c.competitorName })}
          </DisplayHeading>
        </div>

        <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {c.migration.map((step) => (
            <li key={step.step} className="border-l-2 border-foreground bg-background pl-5 pr-3 py-4">
              <Badge variant="outline" className="text-[10px]">
                {t("marketing.pages.comparisons.migration.stepLabel", { n: step.step })}
              </Badge>
              <h3 className="mt-3 text-base font-medium tracking-tight">{locText(step.title, locale)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {locText(step.body, locale)}
              </p>
            </li>
          ))}
        </ol>
      </Section>

      {/* Final CTA */}
      <CTABanner
        headline={t("marketing.pages.comparisons.cta.headline")}
        subhead={t("marketing.pages.comparisons.cta.subhead")}
        primary={{
          label: t("marketing.cta.startTrial"),
          to: withUtm("/dpadmin?intent=signup", { source: "comparison", campaign: c.slug }),
        }}
        secondary={{
          label: t("marketing.cta.viewPricing"),
          to: withUtm("/tarifs", { source: "comparison", campaign: c.slug, content: "cta-banner" }),
        }}
        tone="ink"
      />
    </>
  );
}

function VerdictCell({ cell, locale }: { cell: CellVerdict; locale: Locale }) {
  if (cell.kind === "yes") {
    return (
      <span className="inline-flex items-center gap-2">
        <Check className="h-4 w-4 text-emerald-600" aria-label={locale !== "fr" ? "Yes" : "Oui"} />
        {cell.note && <span className="text-xs text-muted-foreground">{locText(cell.note, locale)}</span>}
      </span>
    );
  }
  if (cell.kind === "no") {
    return <X className="h-4 w-4 text-muted-foreground/50" aria-label={locale !== "fr" ? "No" : "Non"} />;
  }
  if (cell.kind === "partial") {
    return (
      <span className="inline-flex items-center gap-2">
        <Minus className="h-4 w-4 text-amber-600" aria-label={locale !== "fr" ? "Partial" : "Partiel"} />
        {cell.note && <span className="text-xs text-muted-foreground">{locText(cell.note, locale)}</span>}
      </span>
    );
  }
  return <span className="text-sm">{locText(cell.value, locale)}</span>;
}
